import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { fetchAdminStats, fetchAdminUsers } from '../api';
import Card from './ui/Card';
import Input from './ui/Input';

function formatDate(value) {
    if (!value) return '—';
    try {
        return new Date(value).toLocaleString('pt-BR', {
            dateStyle: 'short',
            timeStyle: 'short',
        });
    } catch {
        return '—';
    }
}

function AdminDashboard() {
    const { token } = useAuth();
    const [roomsTotal, setRoomsTotal] = useState(null);
    const [users, setUsers] = useState([]);
    const [usersTotal, setUsersTotal] = useState(0);
    const [search, setSearch] = useState('');
    const [loadingStats, setLoadingStats] = useState(true);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!token) return;
        let cancelled = false;

        (async () => {
            setLoadingStats(true);
            setError(null);
            try {
                const data = await fetchAdminStats(token);
                if (!cancelled) {
                    setRoomsTotal(data.roomsCreatedTotal ?? 0);
                }
            } catch {
                if (!cancelled) {
                    setError('Não foi possível carregar as estatísticas.');
                }
            } finally {
                if (!cancelled) setLoadingStats(false);
            }
        })();

        return () => { cancelled = true; };
    }, [token]);

    const loadUsers = useCallback(async (query) => {
        if (!token) return;
        setLoadingUsers(true);
        setError(null);
        try {
            const data = await fetchAdminUsers(token, { q: query });
            setUsers(data.users || []);
            setUsersTotal(data.total ?? 0);
        } catch {
            setError('Não foi possível carregar os usuários.');
            setUsers([]);
            setUsersTotal(0);
        } finally {
            setLoadingUsers(false);
        }
    }, [token]);

    useEffect(() => {
        if (!token) return;
        const timer = setTimeout(() => {
            loadUsers(search);
        }, 300);
        return () => clearTimeout(timer);
    }, [token, search, loadUsers]);

    return (
        <div className="w-full max-w-5xl mx-auto z-10 flex flex-col gap-6 pb-10">
            <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-slate-100">
                    Painel administrativo
                </h1>
                <p className="text-slate-400 mt-1">
                    Visão geral da plataforma Pleini Pouquer
                </p>
            </div>

            {error && (
                <Card className="p-4 border border-red-500/40 bg-red-950/30">
                    <p className="text-red-300 text-sm">{error}</p>
                </Card>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Card className="p-6 border border-primary/30 bg-gradient-to-br from-primary/10 to-transparent">
                    <p className="text-sm font-medium text-slate-400 uppercase tracking-wide">
                        Salas criadas
                    </p>
                    <p className="text-4xl font-bold text-slate-50 mt-2 tabular-nums">
                        {loadingStats ? '…' : (roomsTotal ?? 0).toLocaleString('pt-BR')}
                    </p>
                    <p className="text-xs text-slate-500 mt-2">Total acumulado até hoje</p>
                </Card>
                <Card className="p-6 border border-white/10">
                    <p className="text-sm font-medium text-slate-400 uppercase tracking-wide">
                        Usuários cadastrados
                    </p>
                    <p className="text-4xl font-bold text-slate-50 mt-2 tabular-nums">
                        {loadingUsers && !search ? '…' : usersTotal.toLocaleString('pt-BR')}
                    </p>
                    <p className="text-xs text-slate-500 mt-2">Quem já fez login com Google</p>
                </Card>
            </div>

            <Card className="p-6">
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
                    <div>
                        <h2 className="text-lg font-semibold text-slate-100">Usuários</h2>
                        <p className="text-sm text-slate-400 mt-1">
                            Pesquise por e-mail ou nome
                        </p>
                    </div>
                    <Input
                        className="sm:max-w-xs w-full"
                        placeholder="Buscar usuário…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        aria-label="Buscar usuário"
                    />
                </div>

                {loadingUsers ? (
                    <p className="text-slate-400 text-center py-8">Carregando usuários…</p>
                ) : users.length === 0 ? (
                    <p className="text-slate-400 text-center py-8">
                        {search ? 'Nenhum usuário encontrado para esta busca.' : 'Nenhum usuário cadastrado ainda.'}
                    </p>
                ) : (
                    <div className="overflow-x-auto -mx-2">
                        <table className="w-full min-w-[640px] text-left text-sm">
                            <thead>
                                <tr className="border-b border-white/10 text-slate-400">
                                    <th className="py-3 px-2 font-medium">Usuário</th>
                                    <th className="py-3 px-2 font-medium">E-mail</th>
                                    <th className="py-3 px-2 font-medium text-right">Salas criadas</th>
                                    <th className="py-3 px-2 font-medium">Último login</th>
                                </tr>
                            </thead>
                            <tbody>
                                {users.map((u) => (
                                    <tr
                                        key={u.googleSub}
                                        className="border-b border-white/5 hover:bg-white/5 transition-colors"
                                    >
                                        <td className="py-3 px-2">
                                            <div className="flex items-center gap-3">
                                                {u.pictureUrl ? (
                                                    <img
                                                        src={u.pictureUrl}
                                                        alt=""
                                                        className="w-9 h-9 rounded-full ring-1 ring-white/20 shrink-0"
                                                        referrerPolicy="no-referrer"
                                                    />
                                                ) : (
                                                    <div
                                                        className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-slate-200 shrink-0"
                                                        aria-hidden
                                                    >
                                                        {(u.displayName || u.email || '?').charAt(0).toUpperCase()}
                                                    </div>
                                                )}
                                                <span className="text-slate-100 font-medium truncate max-w-[160px]">
                                                    {u.displayName || '—'}
                                                    {u.isAdmin && (
                                                        <span className="ml-2 text-xs text-amber-400 font-normal">admin</span>
                                                    )}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="py-3 px-2 text-slate-300 truncate max-w-[200px]" title={u.email}>
                                            {u.email}
                                        </td>
                                        <td className="py-3 px-2 text-right tabular-nums text-slate-100 font-semibold">
                                            {u.roomsCreatedCount ?? 0}
                                        </td>
                                        <td className="py-3 px-2 text-slate-400 whitespace-nowrap">
                                            {formatDate(u.lastLoginAt)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Card>
        </div>
    );
}

export default AdminDashboard;
